import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { Ellipsis } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

/*
  RowActions — the single "⋯" button at the end of a row / card, opening a menu.
  Safe actions first; `danger` items are always moved to the bottom, after a separator, in red.
  No trash icons or red buttons directly in rows.

    <RowActions
      label={`Actions for ${project.name}`}
      items={[
        { label: 'Open project', icon: FolderOpen, onSelect: open },
        { label: 'Edit details', icon: Pencil, onSelect: edit, shortcut: 'E' },
        canDelete && { label: 'Delete project…', icon: Trash2, onSelect: remove, danger: true },
        { label: 'Delete project…', icon: Trash2, danger: true, disabled: true, hint: 'Admins' },
      ]}
    />

  Item: { label, icon, onSelect, danger, disabled, shortcut, hint, hidden, key, testId }.
  Falsy items are skipped, so `cond && {...}` works. Keyboard: Enter/Space/↓ opens, arrows move,
  Esc closes. onSelect runs after the menu has closed, so it may open a dialog (useConfirm) safely.
  Clicks on the button, and Enter/Space/↓ on it, never reach a clickable / keyboard-activatable row;
  nor does any key pressed inside the menu (React events bubble through portals). Your own
  onClick / onKeyDown props still run.
*/

// Keys the trigger handles itself (Radix opens the menu on these).
const TRIGGER_KEYS = new Set(["Enter", " ", "ArrowDown"])

const itemClasses =
  "relative flex h-[34px] cursor-default select-none items-center gap-2.5 rounded-lg px-2.5 text-[13px] text-ink outline-none transition-colors focus:bg-surface-hover data-[highlighted]:bg-surface-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:h-[15px] [&>svg]:w-[15px] [&>svg]:shrink-0 [&>svg]:text-ink-3"

const RowActions = React.forwardRef(({
  items = [],
  label = "More actions",
  align = "end",
  size = "sm",
  className,
  contentClassName,
  onOpenChange,
  disabled,
  "data-testid": testId,
  onClick,
  onKeyDown,
  ...props
}, ref) => {
  const visible = items.filter((item) => item && !item.hidden)
  const safeItems = visible.filter((item) => !item.danger)
  const dangerItems = visible.filter((item) => item.danger)

  if (!visible.length) return null

  const renderItem = (item, index) => {
    const Icon = item.icon
    return (
      <DropdownMenuPrimitive.Item
        key={item.key || `${item.label}-${index}`}
        disabled={item.disabled}
        data-testid={item.testId}
        data-danger={item.danger ? "true" : undefined}
        className={cn(
          itemClasses,
          item.danger && "text-status-red-ink focus:bg-status-red-bg data-[highlighted]:bg-status-red-bg [&>svg]:text-status-red-ink"
        )}
        onSelect={() => {
          if (!item.onSelect) return
          // Let the menu close and restore focus before the action opens anything else.
          setTimeout(() => item.onSelect(), 0)
        }}
      >
        {Icon ? <Icon aria-hidden="true" /> : null}
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.hint ? (
          <span className="ml-3 shrink-0 text-[11.5px] font-medium text-ink-3">{item.hint}</span>
        ) : null}
        {item.shortcut ? <kbd className="ui-kbd ml-3 shrink-0">{item.shortcut}</kbd> : null}
      </DropdownMenuPrimitive.Item>
    )
  }

  return (
    <DropdownMenuPrimitive.Root modal={false} onOpenChange={onOpenChange}>
      <DropdownMenuPrimitive.Trigger asChild disabled={disabled}>
        <Button
          ref={ref}
          type="button"
          variant="ghost"
          size={size === "sm" ? "icon-xs" : "icon"}
          aria-label={label}
          title={label}
          data-testid={testId}
          className={cn("text-ink-3 data-[state=open]:bg-surface-hover data-[state=open]:text-ink", className, "data-[state=open]:opacity-100")}
          {...props}
          onClick={(event) => {
            event.stopPropagation()
            onClick?.(event)
          }}
          onKeyDown={(event) => {
            if (TRIGGER_KEYS.has(event.key)) event.stopPropagation()
            onKeyDown?.(event)
          }}
        >
          <Ellipsis aria-hidden="true" />
        </Button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align={align}
          sideOffset={4}
          collisionPadding={8}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          className={cn(
            "z-50 min-w-[200px] max-w-[320px] overflow-hidden rounded-xl border border-line bg-surface p-1.5 text-ink shadow-overlay",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            contentClassName
          )}
        >
          {safeItems.map(renderItem)}
          {safeItems.length && dangerItems.length ? (
            <DropdownMenuPrimitive.Separator className="mx-0 my-1 h-px bg-line-2" />
          ) : null}
          {dangerItems.map(renderItem)}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  )
})
RowActions.displayName = "RowActions"

export { RowActions }
